<script>
  /**
   * Préstamos entre personas: lo que te deben y lo que debes.
   *
   * ── POR QUÉ PRESTAR NO ES UN GASTO ──────────────────────────────────────────
   *
   * Cuando le prestas $500.000 a alguien, no te empobreciste: esa plata sigue
   * siendo tuya, solo que ahora la tiene otro. Por eso un préstamo se registra
   * como TRASLADO a una cuenta especial, "Me deben", y el patrimonio no se
   * mueve. El día que uno dice "déjalo así", ahí sí es gasto.
   *
   * ── LO QUE SÍ CUESTA: EL 4x1000 ─────────────────────────────────────────────
   *
   * Prestar no es gasto, pero si la plata sale del banco, el banco cobra el
   * impuesto igual. La primera versión de esta pantalla no lo calculaba: a
   * Esteban le faltaron $800 en un pago a Carolay y $2.052 en el préstamo a
   * Lorena. Ahora toda salida pasa por lib/gmf.js, se muestra antes de
   * guardar, y se puede decir "no me lo cobraron".
   *
   * ── CORREGIR DESPUÉS DE GUARDAR ─────────────────────────────────────────────
   *
   * Todo se puede tocar para corregirlo: el préstamo (fecha, monto, persona,
   * cuenta) y cada pago. El saldo NUNCA se edita a mano: se deduce del
   * historial con recalcular(). Así corregir un pago viejo deja bien todos
   * los que siguen, sin que uno tenga que acordarse de qué más cambiar.
   */
  import {
    db, TIPO_CUENTA, METODO_PAGO, crearCuenta, crearDeuda, crearAbono, crearMovimiento,
    ESTADO_DEUDA, CLASE_ABONO, hoy,
  } from '../lib/db.js';
  import { formatear, interpretar, calcularGmf } from '../lib/dinero.js';
  import { guardar, borrar } from '../lib/repositorio.js';
  import { gmfDe, movimientoGmf } from '../lib/gmf.js';
  import {
    planearAbono, movimientosDeAbono, movimientosDeCierre,
    historial, resumen, totales, estadoDe, fechaDePrestamo,
    recalcular, ajusteDeLegado, movimientosDelPrestamo, movimientosDelAbono, validarFechas,
    claveOrden,
    RESULTADO, DESTINO_SOBRANTE, CIERRE,
  } from '../lib/deudas.js';

  let {
    abierta = $bindable(false),
    enfocar = $bindable(null),   // id de una deuda para abrirla directo
    prestamos = [],
    cuentas = [],
    entidades = [],
    abonos = [],
    alCambiar = () => {},
  } = $props();

  /**
   * La hora del movimiento. Si se escribió, esa. Si no, y es de hoy, la de
   * ahora. Si es de otro día y no se sabe, sin hora: inventarla sería dato falso.
   */
  function horaFinal(fecha, hora) {
    if (hora) return hora;
    return fecha === hoy() ? new Date().toTimeString().slice(0, 5) : null;
  }

  // lista | nuevo | editar | detalle | abonar | cerrar
  let vista = $state('lista');
  let elegido = $state(null);
  let guardando = $state(false);
  let verCerrados = $state(false);
  let error = $state('');

  /**
   * Los movimientos de la deuda abierta.
   * Se leen de la base y no de la lista de la pantalla principal, porque esa
   * solo trae los últimos 200: un préstamo de hace meses no estaría ahí.
   */
  let movsDeuda = $state([]);
  let versionMovs = $state(0);

  // ── Formulario del préstamo (nuevo o corregido) ──
  let meDeben = $state(true);
  let persona = $state('');
  let montoTexto = $state('');
  let cuentaId = $state(null);
  let fecha = $state(hoy());
  let fechaEsperada = $state('');
  let horaPrestamo = $state('');
  let notas = $state('');

  // ── Formulario de un pago (nuevo o corregido) ──
  let pagoElegido = $state(null);     // la fila de `abonos` que se corrige
  let abonoTexto = $state('');
  let cuentaAbono = $state(null);
  let fechaAbono = $state(hoy());
  let horaAbono = $state('');
  let proximaFecha = $state('');
  let destinoSobrante = $state(DESTINO_SOBRANTE.REGALO);
  let notaAbono = $state('');

  // ── Compartido: cómo se movió la plata y su 4x1000 ──
  let metodo = $state(METODO_PAGO.TRANSFERENCIA);
  let gmfManual = $state(null);        // null = lo calcula la app

  // ── Cierre ──
  let claseCierre = $state(CIERRE.CONDONADA);
  let motivoCierre = $state('');

  const METODOS = [
    [METODO_PAGO.TRANSFERENCIA, 'Transferencia'],
    [METODO_PAGO.QR, 'QR'],
    [METODO_PAGO.PSE, 'PSE'],
    [METODO_PAGO.OTRO, 'Otro'],
  ];

  const monto = $derived(interpretar(montoTexto) ?? 0);
  const abono = $derived(interpretar(abonoTexto) ?? 0);

  const cuentaPorId = $derived(Object.fromEntries(cuentas.map((c) => [c.id, c])));
  const entidadDe = (c) => entidades.find((e) => e.id === c?.entidad_id) ?? null;

  const cuentasReales = $derived(
    cuentas.filter((c) => !c.archivada &&
      ['efectivo', 'ahorros', 'corriente', 'billetera'].includes(c.tipo))
  );

  $effect(() => {
    if (cuentasReales.length && !cuentaId) cuentaId = cuentasReales[0].id;
    if (cuentasReales.length && !cuentaAbono) cuentaAbono = cuentasReales[0].id;
  });

  const t = $derived(totales(prestamos));
  const meDebenLista = $derived(t.vivas.filter((p) => p.me_deben));
  const deboLista = $derived(t.vivas.filter((p) => !p.me_deben));

  const resumenDe = $derived.by(() => {
    const mapa = {};
    for (const p of prestamos) mapa[p.id] = resumen(p, abonos);
    return mapa;
  });

  const abonosDe = (id) => abonos.filter((a) => a.deuda_id === id && !a.borrado_en);

  /** Si la deuda cambió (un pago nuevo, una corrección), releerla. */
  $effect(() => {
    if (!elegido) return;
    const fresca = prestamos.find((p) => p.id === elegido.id);
    if (fresca && fresca !== elegido) elegido = fresca;
  });

  $effect(() => {
    const id = elegido?.id;
    const _v = versionMovs;   // se relee cada vez que algo cambia
    if (!id) { movsDeuda = []; return; }
    db.movimientos.where('deuda_id').equals(id).toArray().then((l) => {
      if (elegido?.id === id) movsDeuda = l.filter((m) => !m.borrado_en);
    });
  });

  /** Abrir una deuda directo, cuando se llega desde un movimiento. */
  $effect(() => {
    if (!enfocar) return;
    const d = prestamos.find((p) => p.id === enfocar);
    if (d) abrirDetalle(d);
    enfocar = null;
  });

  // ── ¿Sale plata del bolsillo en lo que se está anotando? ──
  // Prestar saca plata si te van a deber. Pagar saca plata si eres tú el que
  // debe. Solo cuando sale plata puede haber 4x1000.
  const salePlata = $derived(
    vista === 'nuevo' || vista === 'editar' ? meDeben
    : vista === 'abonar' && elegido ? !elegido.me_deben
    : false
  );
  const cuentaQueSale = $derived(
    vista === 'abonar' ? cuentaPorId[cuentaAbono] : cuentaPorId[cuentaId]
  );
  const montoQueSale = $derived(vista === 'abonar' ? abono : monto);
  const esEfectivo = $derived(cuentaQueSale?.tipo === 'efectivo');
  const metodoReal = $derived(esEfectivo ? METODO_PAGO.EFECTIVO : metodo);

  const gmfAuto = $derived(
    salePlata
      ? gmfDe({
          tipo: 'transferencia', monto: montoQueSale, cuentaOrigen: cuentaQueSale,
          entidad: entidadDe(cuentaQueSale), metodoPago: metodoReal,
        })
      : { monto: 0, razon: null }
  );
  const gmf = $derived(salePlata ? (gmfManual ?? gmfAuto.monto) : 0);

  // ── El pago que se está anotando o corrigiendo: qué va a pasar ──
  /** Los pagos de la deuda sin el que se está corrigiendo. */
  const otrosPagos = $derived(
    elegido ? abonosDe(elegido.id).filter((a) => a.id !== pagoElegido?.id) : []
  );
  /** El saldo que había justo antes de este pago, según su fecha. */
  const saldoAntesDelPago = $derived.by(() => {
    if (!elegido) return 0;
    const clave = claveOrden({ fecha: fechaAbono, hora: horaAbono, id: pagoElegido?.id ?? '￿' });
    const anteriores = otrosPagos.filter((a) => claveOrden(a) < clave);
    const r = recalcular(elegido, anteriores);
    // Deudas viejas sin historial: su saldo guardado es el que vale.
    return anteriores.length || ajusteDeLegado(elegido, abonos) === null
      ? r.saldo_pendiente
      : elegido.saldo_pendiente;
  });
  const previo = $derived(
    elegido ? planearAbono({ ...elegido, saldo_pendiente: saldoAntesDelPago }, abono) : null
  );

  // ── La línea de tiempo, con lo que le falta a cada renglón ──
  const lineaDeTiempo = $derived(elegido ? historial(elegido, abonos) : []);

  /**
   * ¿A este movimiento le faltó el 4x1000?
   * Solo pasa con lo anotado antes de que la app lo calculara: lo nuevo lleva
   * `gmf_revisado`, porque la persona vio la cifra antes de guardar.
   */
  function gmfFaltante(m, hijos) {
    if (!m || m.gmf_revisado) return 0;
    if (hijos.some((h) => h.concepto === '4x1000' && !h.borrado_en)) return 0;
    const c = cuentaPorId[m.cuenta_origen];
    if (!c) return 0;
    return gmfDe({
      tipo: m.tipo, monto: m.monto, cuentaOrigen: c,
      entidad: entidadDe(c), metodoPago: m.metodo_pago,
    }).monto;
  }

  const filasTiempo = $derived.by(() => {
    if (!elegido) return [];
    const delPrestamo = movimientosDelPrestamo(elegido, movsDeuda, abonos);
    return lineaDeTiempo.map((f) => {
      let principal = null;
      let hijos = [];
      if (f.clase === 'prestamo') {
        principal = delPrestamo.principal;
        hijos = delPrestamo.gmf;
      } else if (f.abono_id) {
        const a = abonos.find((x) => x.id === f.abono_id);
        const ms = a ? movimientosDelAbono(a, movsDeuda) : [];
        principal = ms.find((m) => !m.movimiento_padre_id) ?? null;
        hijos = ms.filter((m) => m.movimiento_padre_id);
      }
      const cobrado = hijos.find((h) => h.concepto === '4x1000' && !h.borrado_en);
      return { ...f, faltaGmf: gmfFaltante(principal, hijos), gmfCobrado: cobrado?.monto ?? 0 };
    });
  });

  const avisoFechas = $derived(
    elegido ? validarFechas(fechaDePrestamo(elegido), abonos, elegido.id) : null
  );

  /** El primer pago: la fecha del préstamo no puede ser posterior. */
  const primerPago = $derived(
    elegido
      ? abonosDe(elegido.id).filter((a) => a.clase !== 'ajuste')
          .map((a) => a.fecha).sort()[0] ?? null
      : null
  );

  // ─── Utilidades ───────────────────────────────────────────────────────────

  /**
   * La cuenta espejo donde vive la plata prestada.
   * Se crea sola la primera vez: pedirle al usuario que cree una cuenta
   * llamada "Me deben" antes de poder anotar un préstamo sería absurdo.
   */
  async function cuentaEspejo(paraCobrar) {
    const tipo = paraCobrar ? TIPO_CUENTA.POR_COBRAR : TIPO_CUENTA.POR_PAGAR;
    const existente = cuentas.find((c) => c.tipo === tipo && !c.borrado_en);
    if (existente) return existente;

    const nueva = crearCuenta({
      nombre: paraCobrar ? 'Me deben' : 'Le debo a gente',
      tipo,
      icono: paraCobrar ? '🤝' : '✋',
      orden: 90,
    });
    await guardar('cuentas', nueva);
    return nueva;
  }

  /**
   * Deja el 4x1000 de un movimiento como debe quedar: lo crea, lo corrige o
   * lo quita. Se usa al anotar y al corregir, para que las dos rutas hagan
   * exactamente lo mismo.
   */
  async function ponerGmf(padre, montoGmf, hijos = []) {
    const existentes = hijos.filter((h) => h.concepto === '4x1000' && !h.borrado_en);
    if (montoGmf > 0) {
      if (existentes.length) {
        await guardar('movimientos', {
          ...existentes[0], ...movimientoGmf(padre, montoGmf), id: existentes[0].id,
        });
        for (const x of existentes.slice(1)) await borrar('movimientos', x.id);
      } else {
        await guardar('movimientos', crearMovimiento(movimientoGmf(padre, montoGmf)));
      }
    } else {
      for (const x of existentes) await borrar('movimientos', x.id);
    }
  }

  /**
   * Guarda en la deuda lo que diga su historial.
   *
   * Si la deuda se cerró "dejándola así", el monto perdonado también se
   * ajusta: si se corrige un pago anterior al cierre, lo perdonado cambia,
   * y el movimiento del cierre tiene que decir la cifra nueva.
   */
  async function aplicarHistorial(deuda, pagos, extra = {}) {
    const r = recalcular(deuda, pagos);
    const { filas, ...campos } = r;

    const cierre = pagos.find((a) => a.clase === 'condonacion' || a.clase === 'incobrable');
    if (cierre && cierre.monto !== r.monto_condonado) {
      await guardar('abonos', {
        ...cierre, monto: r.monto_condonado, saldo_antes: r.monto_condonado, saldo_despues: 0,
      });
      const movsCierre = movimientosDelAbono(cierre, await leerMovs(deuda.id));
      for (const m of movsCierre) {
        await guardar('movimientos', { ...m, monto: r.monto_condonado });
      }
    }

    await guardar('deudas', {
      ...deuda,
      ...campos,
      fecha_prestamo: fechaDePrestamo(deuda),
      ...extra,
    });
  }

  async function leerMovs(deudaId) {
    return (await db.movimientos.where('deuda_id').equals(deudaId).toArray())
      .filter((m) => !m.borrado_en);
  }

  function refrescar() {
    versionMovs++;
    alCambiar();
  }

  // ─── Préstamo: anotar y corregir ──────────────────────────────────────────

  function nuevo(deben) {
    meDeben = deben;
    persona = '';
    montoTexto = '';
    fecha = hoy();
    horaPrestamo = '';
    fechaEsperada = '';
    notas = '';
    metodo = METODO_PAGO.TRANSFERENCIA;
    gmfManual = null;
    error = '';
    vista = 'nuevo';
  }

  function abrirDetalle(p) {
    elegido = p;
    error = '';
    vista = 'detalle';
  }

  /** Abre el formulario del préstamo con lo que tiene guardado. */
  function abrirEdicion() {
    const { principal, gmf: hijos } = movimientosDelPrestamo(elegido, movsDeuda, abonos);
    meDeben = Boolean(elegido.me_deben);
    persona = elegido.acreedor;
    montoTexto = String(elegido.monto_original);
    fecha = fechaDePrestamo(elegido) ?? hoy();
    horaPrestamo = principal?.hora ?? elegido.hora_prestamo ?? '';
    fechaEsperada = elegido.proxima_fecha_pago ?? '';
    notas = elegido.notas ?? '';
    // La cuenta real es la del movimiento: la del lado que no es el espejo.
    const real = principal
      ? (meDeben ? principal.cuenta_origen : principal.cuenta_destino)
      : null;
    if (real && cuentaPorId[real]) cuentaId = real;
    metodo = principal?.metodo_pago && principal.metodo_pago !== METODO_PAGO.EFECTIVO
      ? principal.metodo_pago : METODO_PAGO.TRANSFERENCIA;

    // Si ya tenía 4x1000 se respeta; si nunca lo tuvo, la app lo propone.
    const cobrado = hijos.find((h) => h.concepto === '4x1000');
    gmfManual = cobrado ? cobrado.monto : (principal?.gmf_revisado ? 0 : null);
    error = '';
    vista = 'editar';
  }

  async function registrar() {
    if (guardando || !persona.trim() || monto <= 0) return;
    guardando = true;
    error = '';
    try {
      const espejo = await cuentaEspejo(meDeben);
      const deuda = crearDeuda({
        acreedor: persona.trim(),
        tipo: 'personal',
        cuenta_id: espejo.id,
        monto_original: monto,
        saldo_pendiente: monto,
        me_deben: meDeben,
        fecha_prestamo: fecha,
        hora_prestamo: horaFinal(fecha, horaPrestamo),
        proxima_fecha_pago: fechaEsperada || null,
        estado: ESTADO_DEUDA.ACTIVA,
        activa: true,
        notas: notas.trim(),
      });

      // El movimiento: TRASLADO, no gasto. Tu patrimonio no cambia.
      const mov = crearMovimiento({
        tipo: 'transferencia',
        monto,
        fecha,
        hora: horaFinal(fecha, horaPrestamo),
        concepto: meDeben ? `Préstamo a ${persona.trim()}` : `Préstamo de ${persona.trim()}`,
        contraparte: persona.trim(),
        metodo_pago: metodoReal,
        cuenta_origen: meDeben ? cuentaId : espejo.id,
        cuenta_destino: meDeben ? espejo.id : cuentaId,
        deuda_id: deuda.id,
        gmf,
        gmf_revisado: true,
      });
      deuda.movimiento_id = mov.id;

      await guardar('deudas', deuda);
      await guardar('movimientos', mov);
      await ponerGmf(mov, gmf);

      vista = 'lista';
      refrescar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  async function guardarEdicion() {
    if (guardando || !elegido || !persona.trim() || monto <= 0) return;
    error = '';

    const pagos = abonosDe(elegido.id);
    const fechaMal = validarFechas(fecha, pagos);
    if (fechaMal) {
      error = `${fechaMal} Si el pago es el que está mal, tócalo en la lista para corregirlo.`;
      return;
    }
    const cerrada = pagos.some((a) => a.clase === 'condonacion' || a.clase === 'incobrable');
    if (cerrada && monto !== elegido.monto_original) {
      error = 'Esta deuda se cerró sin cobrar. Para cambiar el monto, primero deshaz el cierre ' +
              '(tócalo en la lista de abajo).';
      return;
    }
    const yaPagado = recalcular(elegido, pagos).total_abonado;
    if (monto < yaPagado) {
      error = `${meDeben ? 'Ya te pagó' : 'Ya le pagaste'} ${formatear(yaPagado)}: ` +
              'el préstamo no puede ser menor que eso.';
      return;
    }

    guardando = true;
    try {
      // Las deudas viejas abonaron sin historial: primero se anota eso, o
      // recalcular() partiría del monto nuevo y olvidaría lo ya pagado.
      let pagosVivos = pagos;
      const legado = ajusteDeLegado(elegido, abonos);
      if (legado) {
        const fila = crearAbono(legado);
        await guardar('abonos', fila);
        pagosVivos = [...pagos, fila];
      }

      const nombre = persona.trim();
      const espejo = cuentaPorId[elegido.cuenta_id] ?? (await cuentaEspejo(meDeben));
      const movs = await leerMovs(elegido.id);
      const { principal, gmf: hijos } = movimientosDelPrestamo(elegido, movs, abonos);

      const campos = {
        tipo: 'transferencia',
        monto,
        fecha,
        hora: horaFinal(fecha, horaPrestamo),
        concepto: meDeben ? `Préstamo a ${nombre}` : `Préstamo de ${nombre}`,
        contraparte: nombre,
        metodo_pago: metodoReal,
        cuenta_origen: meDeben ? cuentaId : espejo.id,
        cuenta_destino: meDeben ? espejo.id : cuentaId,
        deuda_id: elegido.id,
        gmf,
        gmf_revisado: true,
      };
      const mov = principal ? { ...principal, ...campos } : crearMovimiento(campos);
      await guardar('movimientos', mov);
      await ponerGmf(mov, gmf, hijos);

      // Si cambió el nombre, que los pagos también lo digan.
      if (nombre !== elegido.acreedor) {
        for (const m of movs) {
          if (m.id === mov.id || m.concepto === '4x1000') continue;
          await guardar('movimientos', {
            ...m,
            contraparte: nombre,
            concepto: (m.concepto || '').replaceAll(elegido.acreedor, nombre),
          });
        }
      }

      const base = {
        ...elegido,
        acreedor: nombre,
        monto_original: monto,
        fecha_prestamo: fecha,
        hora_prestamo: horaFinal(fecha, horaPrestamo),
        notas: notas.trim(),
        movimiento_id: mov.id,
      };
      const activa = recalcular(base, pagosVivos).estado === 'activa';
      await aplicarHistorial(base, pagosVivos, {
        proxima_fecha_pago: activa ? (fechaEsperada || null) : null,
      });

      vista = 'detalle';
      refrescar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  /**
   * Borrar es distinto de cerrar: borrar es para cuando uno se equivocó
   * anotando, y se lleva por delante los movimientos. Cerrar es para cuando
   * pasó de verdad. Por eso están separados y no se llaman igual.
   */
  async function borrarPrestamo() {
    if (!elegido) return;
    if (!confirm(`¿Borrar el préstamo de ${elegido.acreedor}? Se quitan también sus pagos y movimientos. Esto no se puede deshacer.`)) return;
    guardando = true;
    try {
      for (const m of await leerMovs(elegido.id)) await borrar('movimientos', m.id);
      for (const a of abonosDe(elegido.id)) await borrar('abonos', a.id);
      await borrar('deudas', elegido.id);
      vista = 'lista';
      elegido = null;
      refrescar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  // ─── Pagos: anotar, corregir, borrar ──────────────────────────────────────

  function abrirPagoNuevo() {
    pagoElegido = null;
    abonoTexto = String(elegido.saldo_pendiente);
    fechaAbono = hoy();
    horaAbono = '';
    proximaFecha = '';
    notaAbono = '';
    destinoSobrante = DESTINO_SOBRANTE.REGALO;
    metodo = METODO_PAGO.TRANSFERENCIA;
    gmfManual = null;
    error = '';
    vista = 'abonar';
  }

  /** Tocar un renglón del historial abre ese pago para corregirlo. */
  function abrirPago(fila) {
    if (fila.clase === 'prestamo') { abrirEdicion(); return; }
    const a = abonos.find((x) => x.id === fila.abono_id);
    if (!a || a.clase === 'ajuste') return;

    if (a.clase === 'condonacion' || a.clase === 'incobrable') {
      pagoElegido = a;
      error = '';
      vista = 'cerrar';
      return;
    }

    const ms = movimientosDelAbono(a, movsDeuda);
    const principal = ms.find((m) => !m.movimiento_padre_id);
    const cobrado = ms.find((m) => m.movimiento_padre_id && m.concepto === '4x1000');

    pagoElegido = a;
    abonoTexto = String(a.monto);
    fechaAbono = a.fecha;
    horaAbono = a.hora ?? principal?.hora ?? '';
    proximaFecha = a.proxima_fecha_pago ?? '';
    notaAbono = a.nota ?? '';
    destinoSobrante = a.deuda_inversa_id ? DESTINO_SOBRANTE.DEVOLVER : DESTINO_SOBRANTE.REGALO;
    if (a.cuenta_id && cuentaPorId[a.cuenta_id]) cuentaAbono = a.cuenta_id;
    metodo = (a.metodo_pago ?? principal?.metodo_pago) && (a.metodo_pago ?? principal?.metodo_pago) !== METODO_PAGO.EFECTIVO
      ? (a.metodo_pago ?? principal?.metodo_pago) : METODO_PAGO.TRANSFERENCIA;
    gmfManual = cobrado ? cobrado.monto : (principal?.gmf_revisado ? 0 : null);
    error = '';
    vista = 'abonar';
  }

  /**
   * Si un pago de más se "devolvió", abrió una deuda al revés. Al corregir o
   * borrar ese pago, esa deuda tiene que irse también — salvo que ya tenga
   * pagos propios: ahí se le pide al usuario que la arregle primero.
   */
  async function soltarDeudaInversa(pago) {
    if (!pago?.deuda_inversa_id) return true;
    const suyos = abonos.filter((a) => a.deuda_id === pago.deuda_inversa_id && !a.borrado_en);
    if (suyos.length) {
      error = 'Ese pago de más abrió una deuda al revés que ya tiene pagos. Corrige primero esa deuda.';
      return false;
    }
    await borrar('deudas', pago.deuda_inversa_id);
    return true;
  }

  async function guardarPago() {
    if (guardando || !elegido || abono <= 0) return;
    error = '';
    const fechaDelPrestamo = fechaDePrestamo(elegido);
    if (fechaDelPrestamo && fechaAbono < fechaDelPrestamo) {
      error = `El pago no puede ser antes del préstamo (${fechaDelPrestamo}). ` +
              'Si la fecha del préstamo está mal, corrígela tocando el préstamo.';
      return;
    }

    guardando = true;
    try {
      if (pagoElegido && !(await soltarDeudaInversa(pagoElegido))) return;

      // Deuda vieja sin historial: primero el renglón de lo ya abonado.
      let base = otrosPagos;
      if (!pagoElegido) {
        const legado = ajusteDeLegado(elegido, abonos);
        if (legado) {
          const f = crearAbono(legado);
          await guardar('abonos', f);
          base = [...base, f];
        }
      }

      const fila = pagoElegido ? { ...pagoElegido } : crearAbono({ deuda_id: elegido.id });
      const plan = previo;
      const deudaEnEseMomento = { ...elegido, saldo_pendiente: plan.saldoAntes };

      // Corregir = quitar los movimientos viejos del pago y hacerlos de nuevo.
      // Es más simple y más seguro que tratar de adivinar qué cambió.
      if (pagoElegido) {
        for (const m of movimientosDelAbono(pagoElegido, movsDeuda)) {
          await borrar('movimientos', m.id);
        }
      }

      const espejo = cuentaPorId[elegido.cuenta_id];
      const contrario = plan.sobrante > 0 && destinoSobrante === DESTINO_SOBRANTE.DEVOLVER
        ? await cuentaEspejo(!elegido.me_deben)
        : null;

      const { movimientos } = movimientosDeAbono(deudaEnEseMomento, {
        monto: abono,
        cuentaId: cuentaAbono,
        espejoId: espejo?.id,
        espejoContrarioId: contrario?.id,
        fecha: fechaAbono,
        hora: horaFinal(fechaAbono, horaAbono),
        destinoSobrante,
      });

      let primero = null;
      for (const m of movimientos) {
        const { razon, ...campos } = m;
        const nuevo = crearMovimiento({
          ...campos,
          hora: horaFinal(fechaAbono, horaAbono),
          metodo_pago: metodoReal,
          deuda_id: elegido.id,
          abono_id: fila.id,
          gmf: primero ? 0 : gmf,
          gmf_revisado: true,
        });
        await guardar('movimientos', nuevo);
        primero ??= nuevo;
      }
      // Un pago es UNA transacción en el banco: el 4x1000 va una sola vez,
      // sobre todo lo que salió, colgado del primer movimiento.
      if (primero) await ponerGmf(primero, gmf);

      // Si lo que sobró se devuelve, nace la deuda al revés.
      let inversa = null;
      if (plan.sobrante > 0 && destinoSobrante === DESTINO_SOBRANTE.DEVOLVER) {
        inversa = crearDeuda({
          acreedor: elegido.acreedor,
          tipo: 'personal',
          cuenta_id: contrario.id,
          monto_original: plan.sobrante,
          saldo_pendiente: plan.sobrante,
          me_deben: !elegido.me_deben,
          fecha_prestamo: fechaAbono,
          deuda_origen_id: elegido.id,
          notas: `Quedó de un pago de más sobre ${formatear(elegido.monto_original)}.`,
        });
        await guardar('deudas', inversa);
      }

      const guardada = {
        ...fila,
        clase: plan.sobrante > 0 ? CLASE_ABONO.SOBREPAGO : CLASE_ABONO.ABONO,
        fecha: fechaAbono,
        hora: horaFinal(fechaAbono, horaAbono),
        monto: abono,
        cuenta_id: cuentaAbono,
        metodo_pago: metodoReal,
        movimiento_id: primero?.id ?? null,
        saldo_antes: plan.saldoAntes,
        saldo_despues: plan.saldoDespues,
        proxima_fecha_pago: plan.saldoDespues > 0 ? (proximaFecha || null) : null,
        deuda_inversa_id: inversa?.id ?? null,
        nota: notaAbono.trim(),
      };
      await guardar('abonos', guardada);
      await aplicarHistorial(elegido, [...base, guardada]);

      vista = 'detalle';
      pagoElegido = null;
      refrescar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  async function borrarPago() {
    if (guardando || !pagoElegido) return;
    const esCierre = pagoElegido.clase === 'condonacion' || pagoElegido.clase === 'incobrable';
    const pregunta = esCierre
      ? '¿Deshacer el cierre? La deuda vuelve a quedar viva y el gasto se quita.'
      : `¿Borrar este pago de ${formatear(pagoElegido.monto)}? La deuda vuelve a subir.`;
    if (!confirm(pregunta)) return;

    guardando = true;
    error = '';
    try {
      if (!(await soltarDeudaInversa(pagoElegido))) return;
      for (const m of movimientosDelAbono(pagoElegido, await leerMovs(elegido.id))) {
        await borrar('movimientos', m.id);
      }
      await borrar('abonos', pagoElegido.id);
      await aplicarHistorial(elegido, otrosPagos);

      vista = 'detalle';
      pagoElegido = null;
      refrescar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  // ─── Cerrar sin cobrar ────────────────────────────────────────────────────

  function abrirCierre() {
    pagoElegido = null;
    claseCierre = CIERRE.CONDONADA;
    motivoCierre = '';
    error = '';
    vista = 'cerrar';
  }

  async function cerrarSinCobrar() {
    if (guardando || !elegido) return;
    guardando = true;
    try {
      const fila = crearAbono({ deuda_id: elegido.id });
      const espejo = cuentaPorId[elegido.cuenta_id];
      const { saldo, movimientos } = movimientosDeCierre(elegido, {
        espejoId: espejo?.id,
        fecha: hoy(),
        clase: claseCierre,
      });

      let primero = null;
      for (const m of movimientos) {
        const { razon, ...campos } = m;
        const nuevo = crearMovimiento({ ...campos, hora: horaFinal(hoy(), ''), deuda_id: elegido.id, abono_id: fila.id });
        await guardar('movimientos', nuevo);
        primero ??= nuevo;
      }

      const guardada = {
        ...fila,
        movimiento_id: primero?.id ?? null,
        clase: claseCierre === CIERRE.INCOBRABLE ? 'incobrable' : CLASE_ABONO.CONDONACION,
        fecha: hoy(),
        hora: horaFinal(hoy(), ''),
        monto: saldo,
        saldo_antes: saldo,
        saldo_despues: 0,
        nota: motivoCierre.trim(),
      };
      await guardar('abonos', guardada);
      await aplicarHistorial(elegido, [...abonosDe(elegido.id), guardada], {
        motivo_cierre: motivoCierre.trim(),
      });

      vista = 'detalle';
      motivoCierre = '';
      refrescar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  // ─── Navegación ───────────────────────────────────────────────────────────

  function fechaLarga(iso) {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00')
      .toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }

  const tituloPantalla = $derived(
    vista === 'nuevo' ? (meDeben ? 'Prestar plata' : 'Me prestaron')
    : vista === 'editar' ? 'Corregir el préstamo'
    : vista === 'abonar'
      ? (pagoElegido ? 'Corregir el pago' : elegido?.me_deben ? 'Me pagó' : 'Le pagué')
    : vista === 'cerrar' ? (pagoElegido ? 'El cierre' : 'Cerrar sin cobrar')
    : vista === 'detalle' ? elegido?.acreedor ?? ''
    : 'Préstamos'
  );

  function atras() {
    error = '';
    if (vista === 'abonar' || vista === 'cerrar' || vista === 'editar') {
      pagoElegido = null;
      vista = 'detalle';
    } else if (vista === 'detalle' || vista === 'nuevo') {
      vista = 'lista';
      elegido = null;
    } else {
      abierta = false;
    }
  }
</script>

<!-- La línea del 4x1000: se muestra siempre que sale plata, antes de guardar. -->
{#snippet lineaGmf(montoBase)}
  {#if salePlata && montoBase > 0}
    <div class="gmf">
      {#if gmf > 0}
        <span>4x1000: <strong class="cifra">{formatear(gmf)}</strong></span>
        <button class="enlace" onclick={() => (gmfManual = 0)}>No me lo cobraron</button>
      {:else}
        <span class="sin-gmf">
          Sin 4x1000
          {#if gmfManual === 0}(dijiste que no te lo cobraron){:else if gmfAuto.razon}({gmfAuto.razon}){/if}
        </span>
        {#if gmfManual === 0 && gmfAuto.monto > 0}
          <button class="enlace" onclick={() => (gmfManual = null)}>Sí me lo cobraron</button>
        {:else if gmfAuto.monto === 0 && !esEfectivo}
          <button class="enlace" onclick={() => (gmfManual = calcularGmf(montoBase))}>Sí me lo cobraron</button>
        {/if}
      {/if}
    </div>
  {/if}
{/snippet}

{#snippet elegirMetodo()}
  {#if !esEfectivo}
    <div class="chips" role="radiogroup" aria-label="Cómo se movió la plata">
      {#each METODOS as [valor, etiqueta]}
        <button
          class="chip" class:activo={metodo === valor}
          role="radio" aria-checked={metodo === valor}
          onclick={() => { metodo = valor; if (gmfManual !== 0) gmfManual = null; }}
        >{etiqueta}</button>
      {/each}
    </div>
  {/if}
{/snippet}

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={atras} aria-label="Volver">←</button>
      <h1>{tituloPantalla}</h1>
    </header>

    <div class="contenido">
      {#if error}<p class="error" role="alert">{error}</p>{/if}

      <!-- ══ LISTA ══════════════════════════════════════════════════════ -->
      {#if vista === 'lista'}
        <div class="botones">
          <button class="principal" onclick={() => nuevo(true)}>Presté plata</button>
          <button class="secundario" onclick={() => nuevo(false)}>Me prestaron</button>
        </div>

        {#if !t.vivas.length && !t.cerradas.length}
          <div class="vacio">
            <p class="vacio-titulo">No hay préstamos anotados</p>
            <p class="explica">
              Anota lo que le prestas a alguien y lo que te prestan a ti.
              Prestar <strong>no es un gasto</strong>: esa plata sigue siendo
              tuya, solo que la tiene otro. Por eso no te baja el patrimonio.
            </p>
          </div>
        {/if}

        {#if meDebenLista.length}
          <section>
            <h2>Te deben · <span class="cifra">{formatear(t.meDeben)}</span></h2>
            {#each meDebenLista as p (p.id)}
              <button class="fila" onclick={() => abrirDetalle(p)}>
                <span class="icono" aria-hidden="true">🤝</span>
                <div class="datos">
                  <p class="persona">{p.acreedor}</p>
                  <p class="meta" class:tarde={resumenDe[p.id]?.vencida}>{resumenDe[p.id]?.frase}</p>
                </div>
                <span class="cifra monto entra">{formatear(p.saldo_pendiente)}</span>
              </button>
            {/each}
          </section>
        {/if}

        {#if deboLista.length}
          <section>
            <h2>Debes · <span class="cifra">{formatear(t.debo)}</span></h2>
            {#each deboLista as p (p.id)}
              <button class="fila" onclick={() => abrirDetalle(p)}>
                <span class="icono" aria-hidden="true">✋</span>
                <div class="datos">
                  <p class="persona">{p.acreedor}</p>
                  <p class="meta" class:tarde={resumenDe[p.id]?.vencida}>{resumenDe[p.id]?.frase}</p>
                </div>
                <span class="cifra monto debe">{formatear(p.saldo_pendiente)}</span>
              </button>
            {/each}
          </section>
        {/if}

        <!-- Lo cerrado no se borra: es la memoria de a quién le fue bien
             pagando y a quién no. Se guarda plegado para no estorbar. -->
        {#if t.cerradas.length}
          <section>
            <button class="plegable" onclick={() => (verCerrados = !verCerrados)}>
              <span>Ya cerrados · {t.cerradas.length}</span>
              <span class="flecha" class:abierta={verCerrados} aria-hidden="true">›</span>
            </button>

            {#if verCerrados}
              {#if t.perdido > 0}
                <p class="explica perdida">
                  De lo que has prestado, <strong class="cifra">{formatear(t.perdido)}</strong>
                  no volvieron. Eso ya quedó contado como gasto el día que lo cerraste.
                </p>
              {/if}
              {#each t.cerradas as p (p.id)}
                <button class="fila cerrada" onclick={() => abrirDetalle(p)}>
                  <span class="icono" aria-hidden="true">{estadoDe(p) === 'saldada' ? '✓' : '×'}</span>
                  <div class="datos">
                    <p class="persona">{p.acreedor}</p>
                    <p class="meta">{resumenDe[p.id]?.frase}</p>
                  </div>
                  <span class="cifra monto apagado">{formatear(p.monto_original)}</span>
                </button>
              {/each}
            {/if}
          </section>
        {/if}

      <!-- ══ PRÉSTAMO: NUEVO O CORREGIR ═════════════════════════════════ -->
      {:else if vista === 'nuevo' || vista === 'editar'}
        {#if vista === 'nuevo'}
          <p class="explica">
            {#if meDeben}
              La plata sale de tu cuenta pero <strong>sigue siendo tuya</strong>.
              Se anota como traslado, no como gasto, para que tu patrimonio no
              baje por algo que te van a devolver.
            {:else}
              La plata entra a tu cuenta pero <strong>no es tuya</strong>.
              Tienes más efectivo, pero el patrimonio queda igual porque la debes.
            {/if}
          </p>
        {:else}
          <p class="explica">
            Corrige lo que haya quedado mal. El saldo no se toca a mano: se
            vuelve a calcular con los pagos que ya tiene.
          </p>
        {/if}

        <label>
          <span class="etiqueta">{meDeben ? '¿A quién le prestaste?' : '¿Quién te prestó?'}</span>
          <input type="text" bind:value={persona} placeholder="Nombre" />
        </label>

        <label>
          <span class="etiqueta">Cuánto</span>
          <input class="monto-grande cifra" type="text" inputmode="numeric" bind:value={montoTexto} placeholder="0" />
          <span class="ayuda">Puedes escribir "500 mil".</span>
        </label>

        <div class="par">
          <label>
            <span class="etiqueta">{meDeben ? 'Cuándo le prestaste' : 'Cuándo te prestó'}</span>
            <div class="fecha-hora">
              <input type="date" bind:value={fecha} max={primerPago && primerPago < hoy() ? primerPago : hoy()} />
              <input type="time" bind:value={horaPrestamo} aria-label="Hora (opcional)" />
            </div>
          </label>
          {#if vista === 'nuevo' || estadoDe(elegido) === 'activa'}
            <label>
              <span class="etiqueta">{meDeben ? 'Te paga el' : 'Le pagas el'}</span>
              <input type="date" bind:value={fechaEsperada} />
            </label>
          {/if}
        </div>
        {#if vista === 'nuevo'}
          <span class="ayuda">
            Si no quedaron en una fecha, déjala vacía: la app espera un mes
            antes de recordártelo.
          </span>
        {/if}

        <label>
          <span class="etiqueta">{meDeben ? 'De qué cuenta salió' : 'A qué cuenta entró'}</span>
          <select bind:value={cuentaId} onchange={() => { if (gmfManual !== 0) gmfManual = null; }}>
            {#each cuentasReales as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
          </select>
        </label>

        {@render elegirMetodo()}
        {@render lineaGmf(monto)}

        <label>
          <span class="etiqueta">Notas</span>
          <input type="text" bind:value={notas} placeholder="Para qué era, lo que acordaron…" />
        </label>

        <div class="botones">
          <button class="secundario" onclick={atras}>Cancelar</button>
          {#if vista === 'nuevo'}
            <button class="principal" disabled={!persona.trim() || monto <= 0 || guardando} onclick={registrar}>
              {guardando ? 'Guardando…' : 'Anotar'}
            </button>
          {:else}
            <button class="principal" disabled={!persona.trim() || monto <= 0 || guardando} onclick={guardarEdicion}>
              {guardando ? 'Guardando…' : 'Guardar cambios'}
            </button>
          {/if}
        </div>

      <!-- ══ DETALLE: QUÉ PASÓ CON ESTA DEUDA ═══════════════════════════ -->
      {:else if vista === 'detalle' && elegido}
        {@const r = resumenDe[elegido.id] ?? resumen(elegido, abonos)}
        {@const pct = elegido.monto_original
          ? Math.round((r.abonado / elegido.monto_original) * 100) : 0}

        <div class="resumen" class:saldada={r.cerrada}>
          <p class="rotulo">
            {#if r.cerrada}
              {estadoDe(elegido) === 'saldada' ? 'Quedó saldada' : 'Se cerró sin cobrar'}
            {:else}
              {elegido.me_deben ? 'Te debe' : 'Le debes'}
            {/if}
          </p>
          <p class="cifra grande">{formatear(r.cerrada ? elegido.monto_original : r.saldo)}</p>
          <p class="frase" class:tarde={r.vencida}>{r.frase}</p>

          {#if !r.cerrada && r.abonado > 0}
            <div class="barra" aria-hidden="true">
              <div class="lleno" style="width: {pct}%"></div>
            </div>
            <p class="ayuda">
              Van {formatear(r.abonado)} de {formatear(elegido.monto_original)} ({pct}%).
            </p>
          {/if}
          {#if elegido.notas}<p class="notas">{elegido.notas}</p>{/if}
        </div>

        {#if avisoFechas}
          <div class="aviso ojo">
            <p><strong>Las fechas no cuadran.</strong></p>
            <p class="explica">{avisoFechas}</p>
            <button class="secundario" onclick={abrirEdicion}>Corregir la fecha del préstamo</button>
          </div>
        {/if}

        {#if !r.cerrada}
          <div class="botones">
            <button class="principal" onclick={abrirPagoNuevo}>
              {elegido.me_deben ? 'Me pagó' : 'Le pagué'}
            </button>
            <button class="secundario" onclick={abrirCierre}>Cerrar sin cobrar</button>
          </div>
        {/if}

        <section>
          <h2>Qué ha pasado <span class="pista">· toca para corregir</span></h2>
          <ol class="tiempo">
            {#each filasTiempo as f, i (f.abono_id ?? 'prestamo')}
              <li class:cierre={f.clase === 'condonacion' || f.clase === 'incobrable'}>
                <span class="punto" aria-hidden="true"></span>
                <button class="hecho" onclick={() => abrirPago(f)} disabled={f.clase === 'ajuste'}>
                  <span class="hecho-cuerpo">
                    <span class="hecho-titulo">{f.titulo}</span>
                    <span class="hecho-meta">
                      {fechaLarga(f.fecha)}{#if f.hora}{' · '}{f.hora}{/if}
                      {#if f.clase !== 'prestamo'}
                        {#if f.saldo_despues > 0} · quedó debiendo {formatear(f.saldo_despues)}
                        {:else} · quedó en cero{/if}
                      {/if}
                    </span>
                    {#if f.proxima_fecha_pago && f.saldo_despues > 0}
                      <span class="hecho-meta">Quedaron para el {fechaLarga(f.proxima_fecha_pago)}</span>
                    {/if}
                    {#if f.gmfCobrado > 0}
                      <span class="hecho-meta">4x1000: {formatear(f.gmfCobrado)}</span>
                    {/if}
                    {#if f.faltaGmf > 0}
                      <span class="falta-gmf">Falta el 4x1000 ({formatear(f.faltaGmf)}) · toca para revisarlo</span>
                    {/if}
                    {#if f.nota}<span class="hecho-nota">{f.nota}</span>{/if}
                  </span>
                  <span class="cifra hecho-monto">{formatear(f.monto)}</span>
                </button>
              </li>
            {/each}
          </ol>
        </section>

        <button class="peligro" onclick={borrarPrestamo} disabled={guardando}>
          Borrar este préstamo
        </button>
        <p class="ayuda">
          Borrar es para cuando te equivocaste anotando: quita también los
          pagos y movimientos. Si la plata de verdad no volvió, usa "cerrar sin
          cobrar" para que quede contada como pérdida.
        </p>

      <!-- ══ PAGO: NUEVO O CORREGIR ═════════════════════════════════════ -->
      {:else if vista === 'abonar' && elegido}
        <div class="resumen">
          <p class="rotulo">
            {#if pagoElegido}
              Antes de este pago {elegido.me_deben ? 'te debía' : 'le debías'}
            {:else}
              {elegido.me_deben ? 'Te debe' : 'Le debes'}
            {/if}
          </p>
          <p class="cifra grande">{formatear(saldoAntesDelPago)}</p>
        </div>

        <label>
          <span class="etiqueta">{elegido.me_deben ? '¿Cuánto te pagó?' : '¿Cuánto le pagaste?'}</span>
          <input class="monto-grande cifra" type="text" inputmode="numeric" bind:value={abonoTexto} />
          {#if !pagoElegido}<span class="ayuda">Viene puesto el total. Cámbialo si fue un abono.</span>{/if}
        </label>

        {#if !pagoElegido}
          <div class="atajos">
            <button onclick={() => (abonoTexto = String(saldoAntesDelPago))}>Todo</button>
            <button onclick={() => (abonoTexto = String(Math.round(saldoAntesDelPago / 2)))}>La mitad</button>
            <button onclick={() => (abonoTexto = '')}>Borrar</button>
          </div>
        {/if}

        <div class="par">
          <label>
            <span class="etiqueta">Cuándo</span>
            <div class="fecha-hora">
              <input type="date" bind:value={fechaAbono} min={fechaDePrestamo(elegido)} max={hoy()} />
              <input type="time" bind:value={horaAbono} aria-label="Hora (opcional)" />
            </div>
          </label>
          <label>
            <span class="etiqueta">{elegido.me_deben ? 'A qué cuenta entró' : 'De qué cuenta salió'}</span>
            <select bind:value={cuentaAbono} onchange={() => { if (gmfManual !== 0) gmfManual = null; }}>
              {#each cuentasReales as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
            </select>
          </label>
        </div>

        {@render elegirMetodo()}
        {@render lineaGmf(abono)}

        <!-- La consecuencia, EN VIVO. Que uno vea qué va a pasar antes de
             tocar el botón es lo que evita la mitad de los errores. -->
        {#if previo && abono > 0}
          {#if previo.resultado === RESULTADO.PARCIAL}
            <div class="aviso">
              <p><strong>Quedaría debiendo {formatear(previo.saldoDespues)}.</strong></p>
              <label>
                <span class="etiqueta">¿Para cuándo quedaron del resto?</span>
                <input type="date" bind:value={proximaFecha} min={fechaAbono} />
                <span class="ayuda">Opcional, pero es lo que hace que la app te avise.</span>
              </label>
            </div>
          {:else if previo.resultado === RESULTADO.EXACTO}
            <div class="aviso bueno">
              <p><strong>Con esto queda saldada.</strong></p>
            </div>
          {:else}
            <div class="aviso ojo">
              <p>
                <strong>{elegido.me_deben ? 'Te pagó' : 'Le pagaste'}
                {formatear(previo.sobrante)} de más.</strong>
              </p>
              <p class="explica">¿Qué se hace con esa plata?</p>
              <div class="opciones">
                <button
                  class="opcion" class:activa={destinoSobrante === DESTINO_SOBRANTE.REGALO}
                  onclick={() => (destinoSobrante = DESTINO_SOBRANTE.REGALO)}
                >
                  <span class="opcion-titulo">{elegido.me_deben ? 'Me lo dejó' : 'Se lo dejé'}</span>
                  <span class="opcion-nota">
                    Se cuenta como {elegido.me_deben ? 'ingreso' : 'gasto'}:
                    esa plata {elegido.me_deben ? 'te entró' : 'salió'} de verdad.
                  </span>
                </button>
                <button
                  class="opcion" class:activa={destinoSobrante === DESTINO_SOBRANTE.DEVOLVER}
                  onclick={() => (destinoSobrante = DESTINO_SOBRANTE.DEVOLVER)}
                >
                  <span class="opcion-titulo">{elegido.me_deben ? 'Se lo devuelvo' : 'Me lo devuelve'}</span>
                  <span class="opcion-nota">Se abre una deuda al revés por {formatear(previo.sobrante)}.</span>
                </button>
              </div>
            </div>
          {/if}
        {/if}

        <label>
          <span class="etiqueta">Nota</span>
          <input type="text" bind:value={notaAbono} placeholder="Por Nequi, en efectivo…" />
        </label>

        <div class="botones">
          <button class="secundario" onclick={atras}>Cancelar</button>
          <button class="principal" disabled={abono <= 0 || guardando} onclick={guardarPago}>
            {guardando ? 'Guardando…' : pagoElegido ? 'Guardar cambios' : 'Anotar el pago'}
          </button>
        </div>

        {#if pagoElegido}
          <button class="peligro" onclick={borrarPago} disabled={guardando}>Borrar este pago</button>
        {/if}

      <!-- ══ CERRAR SIN COBRAR (o ver/deshacer el cierre) ═══════════════ -->
      {:else if vista === 'cerrar' && elegido}
        {#if pagoElegido}
          <div class="resumen">
            <p class="rotulo">
              {pagoElegido.clase === 'incobrable' ? 'Lo diste por perdido' : elegido.me_deben ? 'Se lo perdonaste' : 'Te lo perdonó'}
              el {fechaLarga(pagoElegido.fecha)}
            </p>
            <p class="cifra grande">{formatear(pagoElegido.monto)}</p>
            {#if pagoElegido.nota}<p class="notas">{pagoElegido.nota}</p>{/if}
          </div>
          <p class="explica">
            Si lo cerraste por error, o al final sí {elegido.me_deben ? 'te pagó' : 'le pagaste'},
            deshaz el cierre: la deuda vuelve a quedar viva y el
            {elegido.me_deben ? 'gasto' : 'ingreso'} se quita.
          </p>
          <div class="botones">
            <button class="secundario" onclick={atras}>Volver</button>
            <button class="principal" disabled={guardando} onclick={borrarPago}>Deshacer el cierre</button>
          </div>
        {:else}
          <div class="resumen">
            <p class="rotulo">Quedan sin {elegido.me_deben ? 'cobrar' : 'pagar'}</p>
            <p class="cifra grande">{formatear(elegido.saldo_pendiente)}</p>
          </div>

          <p class="explica">
            {#if elegido.me_deben}
              Hasta hoy esa plata seguía contando como tuya, porque te la iban a
              devolver. Al cerrarla dejas de contarla: se registra como
              <strong>gasto con la fecha de hoy</strong> y tu patrimonio baja
              {formatear(elegido.saldo_pendiente)}.
            {:else}
              Si te perdonaron lo que debías, eso es <strong>plata que te entró</strong>.
              Se registra como ingreso y tu patrimonio sube
              {formatear(elegido.saldo_pendiente)}.
            {/if}
          </p>

          {#if elegido.me_deben}
            <div class="opciones">
              <button class="opcion" class:activa={claseCierre === CIERRE.CONDONADA}
                      onclick={() => (claseCierre = CIERRE.CONDONADA)}>
                <span class="opcion-titulo">Se lo perdoné</span>
                <span class="opcion-nota">Lo decidiste tú. "Déjalo así".</span>
              </button>
              <button class="opcion" class:activa={claseCierre === CIERRE.INCOBRABLE}
                      onclick={() => (claseCierre = CIERRE.INCOBRABLE)}>
                <span class="opcion-titulo">Lo doy por perdido</span>
                <span class="opcion-nota">No lo perdonaste: te cansaste de cobrar.</span>
              </button>
            </div>
          {/if}

          <label>
            <span class="etiqueta">¿Por qué?</span>
            <input type="text" bind:value={motivoCierre} placeholder="Opcional, pero el año que viene se agradece" />
          </label>

          <div class="botones">
            <button class="secundario" onclick={atras}>Cancelar</button>
            <button class="principal" disabled={guardando} onclick={cerrarSinCobrar}>
              {guardando ? 'Guardando…' : 'Cerrar la deuda'}
            </button>
          </div>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateX(16px) }
    to   { opacity: 1; transform: translateX(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold) }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-4);
  }

  section { display: flex; flex-direction: column; gap: var(--e-2) }
  h2 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }

  .explica { font-size: var(--t-sm); color: var(--ink-2); line-height: var(--lh-loose) }
  .explica.perdida { color: var(--ink-3) }
  .ayuda { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-loose) }

  .vacio { padding: var(--e-8) 0; text-align: center }
  .vacio-titulo {
    font-size: var(--t-lg);
    font-weight: var(--w-semibold);
    color: var(--ink-2);
    margin-bottom: var(--e-2);
  }

  /* ── Fila de préstamo ── */
  .fila {
    display: grid;
    grid-template-columns: 40px 1fr auto;
    gap: var(--e-3);
    align-items: center;
    padding: var(--e-3);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    text-align: left;
    width: 100%;
    transition: transform var(--d-instant) var(--ease-salida);
  }
  .fila:active { transform: scale(0.99) }
  .fila.cerrada { opacity: 0.62 }

  .icono {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-size: var(--t-lg);
  }
  .datos { min-width: 0 }
  .persona { font-weight: var(--w-medium) }
  .meta { font-size: var(--t-xs); color: var(--ink-3) }
  .meta.tarde { color: var(--alerta) }
  .monto { font-weight: var(--w-semibold); white-space: nowrap }
  .monto.entra { color: var(--ingreso) }
  .monto.debe { color: var(--peligro) }
  .monto.apagado { color: var(--ink-3); font-weight: var(--w-medium) }

  .plegable {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--e-2) 0;
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }
  .flecha { transition: transform var(--d-rapido) var(--ease-salida); font-size: var(--t-lg) }
  .flecha.abierta { transform: rotate(90deg) }

  /* ── Resumen de una persona ── */
  .resumen {
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
  }
  .resumen.saldada { opacity: 0.8 }
  .rotulo { font-size: var(--t-sm); color: var(--ink-3) }
  .grande {
    font-size: var(--t-3xl);
    font-weight: var(--w-bold);
    letter-spacing: var(--track-tight);
    line-height: var(--lh-tight);
  }
  .frase { font-size: var(--t-sm); color: var(--ink-2); margin-top: var(--e-1) }
  .frase.tarde { color: var(--alerta) }
  .notas { font-size: var(--t-xs); color: var(--ink-3); margin-top: var(--e-2) }

  .barra {
    height: 6px;
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    margin-top: var(--e-3);
    overflow: hidden;
  }
  .lleno {
    height: 100%;
    background: var(--acento);
    border-radius: var(--r-full);
    transition: width var(--d-normal) var(--ease-salida);
  }

  /* ── Línea de tiempo: cada renglón se toca para corregirlo ── */
  .pista { text-transform: none; letter-spacing: 0; font-weight: var(--w-regular) }
  .tiempo { list-style: none; position: relative; padding-left: var(--e-5) }
  .tiempo::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 14px;
    bottom: 14px;
    width: 1px;
    background: var(--border-strong);
  }
  .tiempo li { position: relative }
  .punto {
    position: absolute;
    left: calc(var(--e-5) * -1 + 1px);
    top: calc(var(--e-3) + 5px);
    width: 9px;
    height: 9px;
    border-radius: var(--r-full);
    background: var(--acento);
    border: 2px solid var(--bg-canvas);
  }
  .tiempo li.cierre .punto { background: var(--alerta) }
  .hecho {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--e-2);
    width: 100%;
    padding: var(--e-3) var(--e-2);
    margin-left: calc(var(--e-2) * -1);
    border-radius: var(--r-md);
    text-align: left;
    transition: background var(--d-instant) var(--ease-salida);
  }
  .hecho:not(:disabled):active { background: var(--bg-sunken) }
  @media (hover: hover) { .hecho:not(:disabled):hover { background: var(--bg-sunken) } }
  .hecho-cuerpo { display: flex; flex-direction: column; gap: 1px; min-width: 0 }
  .hecho-titulo { font-size: var(--t-sm); font-weight: var(--w-medium) }
  .hecho-meta { font-size: var(--t-xs); color: var(--ink-3) }
  .hecho-nota { font-size: var(--t-xs); color: var(--ink-2); font-style: italic; margin-top: 2px }
  .hecho-monto { font-size: var(--t-sm); font-weight: var(--w-semibold); white-space: nowrap }
  .falta-gmf { font-size: var(--t-xs); color: var(--alerta); font-weight: var(--w-medium) }

  /* ── Formularios ── */
  label { display: flex; flex-direction: column; gap: var(--e-1) }
  .etiqueta { font-size: var(--t-sm); font-weight: var(--w-medium) }
  .par { display: grid; grid-template-columns: 1fr 1fr; gap: var(--e-3) }

  input, select {
    padding: var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
    width: 100%;
  }
  .monto-grande { font-size: var(--t-2xl); font-weight: var(--w-bold); text-align: center }

  .atajos { display: flex; gap: var(--e-2) }
  .atajos button {
    flex: 1;
    padding: var(--e-2);
    font-size: var(--t-sm);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    min-height: 40px;
  }

  /* ── El aviso de lo que va a pasar ── */
  .aviso {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    border-left: 3px solid var(--acento);
    display: flex;
    flex-direction: column;
    gap: var(--e-2);
    font-size: var(--t-sm);
    animation: aparecer var(--d-rapido) var(--ease-entrada);
  }
  @keyframes aparecer {
    from { opacity: 0; transform: translateY(-4px) }
    to   { opacity: 1; transform: translateY(0) }
  }
  .aviso.bueno { border-left-color: var(--ingreso) }
  /* Dentro de la caja, el botón secundario tendría el mismo fondo que ella y
     no se leería como botón. */
  .aviso .secundario {
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
  }
  .aviso.ojo { border-left-color: var(--alerta) }

  .opciones { display: flex; flex-direction: column; gap: var(--e-2) }
  .opcion {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
    padding: var(--e-3);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    background: var(--bg-surface);
    transition: border-color var(--d-instant) var(--ease-salida);
  }
  .opcion.activa { border-color: var(--acento); box-shadow: inset 0 0 0 1px var(--acento) }
  .opcion-titulo { font-size: var(--t-sm); font-weight: var(--w-semibold) }
  .opcion-nota { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-loose) }

  .botones { display: flex; gap: var(--e-2) }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--acento);
    color: var(--on-acento);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .principal:disabled { opacity: 0.4 }
  .secundario {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .peligro {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: transparent;
    color: var(--peligro);
    font-weight: var(--w-medium);
    min-height: var(--tap);
    border: 1px solid var(--border-strong);
  }

  /* ── Cómo se movió la plata ── */
  .chips { display: flex; gap: var(--e-2); flex-wrap: wrap }
  .chip {
    min-height: 38px;
    padding: 0 var(--e-3);
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-size: var(--t-sm);
    font-weight: var(--w-medium);
  }
  .chip.activo { background: var(--acento); color: var(--on-acento) }

  /* ── El 4x1000, siempre a la vista antes de guardar ── */
  .gmf {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--e-2);
    flex-wrap: wrap;
    font-size: var(--t-sm);
    padding: var(--e-2) var(--e-3);
    background: var(--bg-sunken);
    border-radius: var(--r-md);
  }
  .sin-gmf { color: var(--ink-3) }
  .enlace {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    color: var(--acento);
    text-decoration: underline;
    text-underline-offset: 2px;
    min-height: 32px;
  }

  .error {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--alerta-suave);
    color: var(--alerta);
    font-size: var(--t-sm);
    line-height: var(--lh-loose);
  }
  /* Fecha y hora juntas: el orden dentro del día también cuenta. */
  .fecha-hora { display: flex; flex-direction: column; gap: var(--e-1) }
</style>
